// `extension-alert-candidates-export.tpl.html` — the export form that opens in the review
// screen's side view.
//
// ⚠️ THE TWO BLOCK CLASS NAMES ARE INVERTED RELATIVE TO WHAT THEY DO, and they are upstream's,
// so they are copied verbatim:
//
//     .extension-candidate-export-form-new-list            ng-show="!createNewList"  ← the
//         EXISTING-list picker ("Search for a Hotlist/Tearsheet")
//     .extension-candidate-export-form-extension-candidate-export-form-existing-list
//                                                          ng-show="!!createNewList" ← the
//         NEW-list name input, plus the privacy radios
//
// The second also carries its own prefix twice. Renaming either to match its behaviour — which
// is the instinct on reading them — detaches it from the stylesheet.
import { createElement, useEffect, useRef, useState, type ReactElement } from 'react'
import { WooRadio } from '../common/WooRadio.tsx'

/**
 * `candidateListsCopyOptions` — what this ATS calls a candidate list.
 *
 * ⚠️ TWO ATSes HAVE A NAME FOR IT AND EVERYTHING ELSE FALLS BACK. The word appears SIX times in
 * this panel (the heading, both radios, two labels, the privacy heading), so getting the
 * fallback wrong is visible six times over.
 */
export const CANDIDATE_LIST_COPY: Readonly<Record<string, string>> = {
  Bullhorn: 'Hotlist/Tearsheet',
  JobDiva: 'Hotlist',
}

export const CANDIDATE_LIST_COPY_DEFAULT = 'candidate list'

export function candidateListCopy(atsName: string | undefined): string {
  if (atsName === undefined) return CANDIDATE_LIST_COPY_DEFAULT
  return CANDIDATE_LIST_COPY[atsName] ?? CANDIDATE_LIST_COPY_DEFAULT
}

/**
 * `clientContactListsCopyOptions` (`extension-alert-client-contacts-export.component.ng1.ts:48`):
 * the same two ATS names, and a DIFFERENT fallback. Found 28 Oct against the re-captured
 * reference: a Vincere contact's panel said "candidate list" six times where upstream says
 * "contact list".
 */
export const CLIENT_CONTACT_LIST_COPY_DEFAULT = 'contact list'

export function exportListCopy(atsName: string | undefined, entity: ExportEntity): string {
  if (entity === 'candidates') return candidateListCopy(atsName)
  if (atsName === undefined) return CLIENT_CONTACT_LIST_COPY_DEFAULT
  return CANDIDATE_LIST_COPY[atsName] ?? CLIENT_CONTACT_LIST_COPY_DEFAULT
}

/**
 * Whether the new list offers Private / Public. "Only Bullhorn currently supports
 * private/public lists for candidates" upstream, but the CONTACTS panel has no such gate: its
 * template draws the radios for every ATS, and its submit refuses an unanswered pair.
 */
export function showsListPrivacy(atsName: string | undefined, entity: ExportEntity): boolean {
  return entity === 'clientContacts' || atsName === 'Bullhorn'
}

/**
 * `getCandidateCTAText` — what the heading exports.
 *
 * ⚠️ ONE SELECTED CANDIDATE IS NAMED, not counted. None reads "candidates", one reads that
 * candidate's NAME, and two or more read "N candidates" — so the singular case is not
 * "1 candidate".
 */
export function candidateCtaText(
  checked: ReadonlyArray<{ name?: string | null }>,
  entity: ExportEntity = 'candidates',
): string {
  // ⚠️ THE NOUN IS THE ONLY DIFFERENCE BETWEEN THE TWO PRODUCTS' PANELS. `getClientContactCTAText`
  // is `getCandidateCTAText` with "contacts" for "candidates" — and the two TEMPLATES are
  // byte-identical apart from tabs against spaces and this one call. One component serves both.
  const plural = entity === 'clientContacts' ? 'contacts' : 'candidates'
  if (checked.length === 0) return plural
  if (checked.length === 1) return checked[0]?.name ?? plural
  return `${String(checked.length)} ${plural}`
}

/** Which product's panel this is; see `candidateCtaText`. */
export type ExportEntity = 'candidates' | 'clientContacts'

export interface CandidateExportPanelProps {
  /**
   * `candidates` renders `<extension-alert-candidates-export>`, `clientContacts` renders
   * `<extension-alert-client-contacts-export>`. Neither element carries a rule of its own —
   * both are only the `flex-basis` carrier — but the type is what upstream writes, and a
   * later stylesheet that reaches for one would find it.
   */
  entity?: ExportEntity
  /** An identity: the list noun and the privacy radios key on it. */
  atsName?: string
  /** `ctrl.atsLabel` — the heading's ATS (5cfd3ef899). Defaults to `atsName`. */
  atsLabel?: string
  /** The checked rows — only their count and, for exactly one, its name. */
  checked?: ReadonlyArray<{ name?: string | null }>
  /** `alertName` seeds the new-list name: "Hunter name serves as a default name". */
  alertName?: string
  /**
   * `!!ctrl.hunterContainer` — false on the standalone profile (SINGLE_PROFILE_EXPORT,
   * cee70a51e8), where there is no hunter: the disclaimer says "Profile name serves as a default
   * name", and `alertName` is the profile's name (`profileDefaultListName`).
   */
  fromHunter?: boolean
  /**
   * `ExtensionViewWidth.Extended` (378), as a `flex-basis` on the ELEMENT.
   *
   * ⚠️ FIFTH STATE, FIFTH READING. The side view itself is `flex: auto 1 1` once `rendered`,
   * so without this the panel takes whatever the frame has left — 547px at a 700px viewport,
   * where production has 378 — and the heading wraps differently, which moves everything below
   * it. The width is never in the stylesheet; read the state's `ng-style` every time.
   */
  flexBasis?: number
  onClose?: () => void
  /**
   * ⚠️ IT CARRIES THE FORM, AND IT USED TO CARRY NOTHING. `onSubmit?: () => void` could not
   * describe an export: which list, under what name, public or private — all of it lives in
   * this component's state, so a caller that wanted to post had no way to learn any of it.
   * That is why `exportToAtsList` sat in `packages/api` with zero callers.
   */
  onSubmit?: (values: ExportSubmitValues) => void
  /**
   * `getShortlistAutocomplete($viewValue)` behind `uib-typeahead`. Absent, the existing-list
   * field is a dead text box — which is what `typeahead-editable="false"` makes it anyway
   * until something is PICKED, so the validation message below is the honest state rather
   * than a degraded one.
   */
  onSearchLists?: (query: string) => Promise<readonly ExportListOption[]>
  /** In flight — the button stops a second press from starting a second export. */
  submitting?: boolean
  /** The server's own words, when it answered and refused. */
  error?: string
}

/** `IExtensionCandidateListSearchDto` — `{ id, text }`, as the ats-filters endpoint returns it. */
export interface ExportListOption {
  id: string
  text: string
}

/**
 * What `onSubmit` hands back, shaped as the two writes rather than as the form.
 *
 * ⚠️ THE UNION IS UPSTREAM'S AND IT IS THE CONTRACT — see `CandidateExportRequest`. The panel
 * decides which of the two the user described; the caller only adds the ids.
 */
export type ExportSubmitValues =
  | { createNewList: true; name: string; isPublic: boolean }
  | { createNewList: false; listId: string }

export function CandidateExportPanel({
  entity = 'candidates',
  atsName,
  atsLabel = atsName,
  checked = [],
  alertName = '',
  fromHunter = true,
  flexBasis,
  onClose,
  onSubmit,
  onSearchLists,
  submitting = false,
  error,
}: CandidateExportPanelProps): ReactElement {
  // `this.createNewList = true` in the constructor — the panel opens on "create".
  const [createNewList, setCreateNewList] = useState(true)
  // ⚠️ `undefined`, NOT false. The privacy radios open with NEITHER selected, and submitting
  // without choosing is a validation failure — a default of Private would silently make every
  // exported list private.
  const [isNewListPublic, setIsNewListPublic] = useState<boolean | undefined>(undefined)
  const [listText, setListText] = useState('')
  // ⚠️ `useState(alertName)` IS NOT ENOUGH, and it looks like it is. The alert arrives from a
  // request, so the first render has no name and `useState`'s initial value is captured then —
  // the field stays empty for the rest of its life while the heading beside it fills in.
  // Upstream binds `ng-model="ctrl.alertName"` to a field its bindings deliver, so it simply
  // appears. This follows the prop until the user types, and stops the moment they do.
  const [listName, setListName] = useState(alertName)
  const edited = useRef(false)
  useEffect(() => {
    if (!edited.current) setListName(alertName)
  }, [alertName])

  const copy = exportListCopy(atsName, entity)
  const showPrivacyOption = showsListPrivacy(atsName, entity)

  /**
   * `form.$setSubmitted()` — every validation message below is `$error.x && form.$submitted`,
   * so nothing goes red until Export has been pressed once. A panel that validates on change
   * marks the privacy radios invalid before the user has reached them.
   */
  const [submitted, setSubmitted] = useState(false)
  // `typeahead-editable="false"`: typing does not select. Only a pick sets this, and only this
  // satisfies `form.list.$error.required` on the existing-list branch.
  const [selectedList, setSelectedList] = useState<ExportListOption | undefined>(undefined)
  const [matches, setMatches] = useState<readonly ExportListOption[]>([])

  const nameMissing = listName.trim() === ''
  const listMissing = selectedList === undefined
  const privacyMissing = showPrivacyOption && isNewListPublic === undefined

  /** `ctrl.onSubmit()` — the three guards in upstream's order. */
  const submit = (): void => {
    setSubmitted(true)
    if (submitting) return
    if (createNewList) {
      // ⚠️ PRIVACY IS CHECKED BEFORE `$invalid`, and separately from it, because
      // `isNewListPublic` is not a form control — the radios bind a controller field, so an
      // unanswered pair leaves the form perfectly valid.
      if (privacyMissing || nameMissing) return
      onSubmit?.({ createNewList: true, name: listName, isPublic: isNewListPublic === true })
      return
    }
    if (listMissing) return
    onSubmit?.({ createNewList: false, listId: selectedList.id })
  }

  const search = (query: string): void => {
    // The model is cleared by typing, exactly as `typeahead-editable="false"` does: a stale
    // pick must not survive an edited query, or Export posts to a list the field no longer
    // shows.
    setSelectedList(undefined)
    if (onSearchLists === undefined || query === '') {
      setMatches([])
      return
    }
    void onSearchLists(query).then(
      (found) => setMatches(found),
      // A failed lookup is an empty list, which already reads as "no match".
      () => setMatches([]),
    )
  }

  const panel = (
    <div className="extension-candidate-export-container">
      <div className="extension-candidate-export-header">
        <div className="extension-candidate-export-x-icon">
          <i
            className="far fa-times fa-lg"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onClose?.()
            }}
          />
        </div>
        <div className="extension-candidate-export-cta">
          {/* ⚠️ ONE EXPRESSION PER LINE, NOT INTERLEAVED CHILDREN. JSX emits a separate text
              node per literal and per expression where AngularJS interpolation emits one, and
              the browser accumulates their advance widths differently — enough to move a glyph
              by a pixel in both of these lines. */}
          {`Export ${candidateCtaText(checked, entity)} to `}
          <br />
          <b>{`${atsLabel ?? ''} ${copy}`}</b>
        </div>
      </div>

      <div className="extension-candidate-export-ats-form">
        <div className="extension-candidate-form-export-container">
          <div className="extension-candidate-form-export-input-container">
            {/* `<radio-ng-value>` and `<radio>` compile to IDENTICAL markup — the only
                difference is `ng-value` against `value`, an expression against a literal — so
                both are this component. */}
            <WooRadio
              name="createNewList"
              value="true"
              checked={createNewList}
              onChange={() => setCreateNewList(true)}
            >
              {`Create a new ${copy}`}
            </WooRadio>
            <WooRadio
              name="createNewList"
              value="false"
              checked={!createNewList}
              onChange={() => setCreateNewList(false)}
            >
              {`Add to an existing ${copy}`}
            </WooRadio>

            {/* `ng-show`, not `ng-if` — both blocks stay mounted and keep their input values
                when the radio flips back. */}
            <div
              className="extension-candidate-export-form-new-list mT20"
              style={createNewList ? { display: 'none' } : undefined}
            >
              <label className="bold-soft extension-candidate-export-label">{copy}</label>
              <label className="input-disclaimer-label">Updated on an hourly basis</label>
              <input
                name="list"
                className={`form-control input extension-candidate-export-input${
                  listMissing && submitted ? ' export-form-error' : ''
                }`}
                type="text"
                placeholder={`Search for a ${copy}`}
                autoComplete="off"
                value={listText}
                onChange={(event) => {
                  setListText(event.target.value)
                  search(event.target.value)
                }}
                onBlur={() => setMatches([])}
              />
              {/* `uib-typeahead`'s own markup: a `dropdown-menu` of `<a>` rows carrying
                  `shortlist-autocomplete.tpl.html`, whose title attribute is the list's ID —
                  which is how a recruiter tells two same-named hotlists apart. Rendered only
                  while there are matches, so a closed panel adds nothing to the reference. */}
              {matches.length === 0 ? null : (
                <ul className="dropdown-menu" style={{ display: 'block' }}>
                  {matches.map((option) => (
                    <li key={option.id}>
                      {/* `mousedown`, not `click` — the input's blur closes the panel first. */}
                      <a
                        tabIndex={-1}
                        onMouseDown={(event) => {
                          event.preventDefault()
                          setSelectedList(option)
                          setListText(option.text)
                          setMatches([])
                        }}
                      >
                        <div className="add-to-ats-list-autocomplete-text" title={option.id}>
                          {option.text}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {listMissing && submitted ? (
                <label className="control-label has-error extension-candidate-export-error-label">
                  {`Please select a ${copy} from the list`}
                </label>
              ) : null}
            </div>

            <div
              className="extension-candidate-export-form-extension-candidate-export-form-existing-list mT20"
              style={createNewList ? undefined : { display: 'none' }}
            >
              <div>
                <label className="bold-soft extension-candidate-export-label">{copy}</label>
                <label className="input-disclaimer-label">{`${fromHunter ? 'Hunter' : 'Profile'} name serves as a default name`}</label>
                <input
                  name="listName"
                  className={`form-control input extension-candidate-export-input${
                    nameMissing && submitted ? ' export-form-error' : ''
                  }`}
                  type="text"
                  placeholder={`Create a ${copy} name`}
                  autoComplete="off"
                  required
                  value={listName}
                  onChange={(event) => {
                    edited.current = true
                    setListName(event.target.value)
                  }}
                />
                {nameMissing && submitted ? (
                  <label className="control-label has-error extension-candidate-export-error-label">
                    {`Please enter a name for the new ${copy}`}
                  </label>
                ) : null}
              </div>
              {showPrivacyOption ? (
                <div className="mT20">
                  <label className="bold-soft extension-candidate-export-label">{copy} privacy</label>
                  <br />
                  <WooRadio
                    name="isNewListPublic"
                    value="false"
                    checked={isNewListPublic === false}
                    onChange={() => setIsNewListPublic(false)}
                  >
                    Private
                  </WooRadio>{' '}
                  <WooRadio
                    name="isNewListPublic"
                    value="true"
                    checked={isNewListPublic === true}
                    onChange={() => setIsNewListPublic(true)}
                  >
                    Public
                  </WooRadio>
                  {/* ⚠️ "Hotlist/Tearsheet" IS HARDCODED UPSTREAM, where every other mention of
                      a list on this panel interpolates `candidateListCopy`. A JobDiva user is
                      told to pick a privacy for something the rest of the screen calls a
                      Hotlist. Copied verbatim: this is a re-platform, and correcting it here
                      would be a divergence the visual gate cannot see. */}
                  {privacyMissing && submitted ? (
                    <label className="control-label has-error extension-candidate-export-error-label">
                      Please select Hotlist/Tearsheet privacy
                    </label>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Empty upstream too — a spacer the stylesheet gives height to. */}
            <div
              className="extension-candidate-export-form-privacy mT20"
              style={createNewList ? undefined : { display: 'none' }}
            />
          </div>

          {/* ⚠️ A WRITE WITH NO VISIBLE OUTCOME IS WORSE THAN NO WRITE — the same rule
              `AddToJobPanel` states. This one reaches the customer's ATS, so the panel has to
              be able to say the server refused, and has to stop a second press while the
              first is in flight. */}
          {error === undefined ? null : (
            <label className="control-label has-error extension-candidate-export-error-label">{error}</label>
          )}
          <div className="add-to-ats-list-button pull-right mT20">
            <button type="button" onClick={submit} disabled={submitting}>
              Export
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createElement(
    entity === 'clientContacts' ? 'extension-alert-client-contacts-export' : 'extension-alert-candidates-export',
    flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
    panel,
  )
}
